import format from "date-fns/format";
import parse from "date-fns/parse";
import type { ReactElement } from "react";
import type { Omit } from "utility-types";
import type { IYearlyBrazilianBankFinancialStatementRow } from "../../../../../types/graphqlTypes";
import FinancialValueThresholdColumnStyles from "../../../../financials/FinancialValueThresholdColumnStyles";
import * as palette from "../../../../palette";
import TextTooltip from "../../../../shared/TextTooltip";
import formatFinancialValue from "../../formatFinancialValue";

interface IYearlyBankFinancialsProps {
  financials: Omit<IYearlyBrazilianBankFinancialStatementRow, "__typename">[];
}

export default function YearlyBankFinancials({ financials }: IYearlyBankFinancialsProps): ReactElement | null {
  if (financials.length === 0) {
    return null;
  }

  return (
    <table
      css={{
        borderRadius: 4,
        borderCollapse: "collapse",
        borderSpacing: 0,
        "> thead > tr > th:first-of-type": {
          borderTopLeftRadius: 4,
        },
        "> thead > tr > th:last-of-type": {
          borderTopRightRadius: 4,
        },
        "> tbody > tr:last-of-type > td:first-of-type": {
          borderBottomLeftRadius: 4,
        },
        "> tbody > tr:last-of-type > td:last-of-type": {
          borderBottomRightRadius: 4,
        },
        "> thead > tr > th": {
          position: "sticky",
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
        "& > tbody > tr > td": {
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
      }}
    >
      <thead>
        <tr>
          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.white,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.blueBayoux,
              borderRight: `solid 1px ${palette.lynch}`,
            }}
          >
            Ano
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.seaGreen,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.lynch}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netEquity" placement="top" content="Ativos - Passivos">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Patrimônio Líquido
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.seaGreen,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/financialIntermediationRevenue"
              placement="top"
              content="Venda de Produtos ou Serviços"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Receita Inter. Fin.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.seaGreen,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/netIncome"
              placement="top"
              content="Receita Líquida - Descontos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Lucro Líquido
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.seaGreen,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/netMargin"
              placement="top"
              content="Lucro Líquido / Receita Líquida"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Margem Líquida
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.seaGreen,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/returnOnEquity"
              placement="top"
              content="Return On Equity (Lucro Líquido / Patrimônio Líquido)"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  ROE
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/bisRatio"
              placement="top"
              content="Índice de Alavancagem (Bom ≥ 11%)"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Índice de Basiléia
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/provisionForCreditLosses"
              placement="top"
              content="Provisão para Devedores Duvidosos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  PDD
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            PDD/Lucro Líquido
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.luxorGold,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/payout"
              placement="top"
              content="Dividendos e JCP Distribuídos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Proventos
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.luxorGold,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/payoutToNetIncomeRatio"
              placement="top"
              content="Proventos / Lucro Líquido"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Payout
                </span>
              )}
            </TextTooltip>
          </th>
        </tr>
      </thead>

      <tbody
        css={{
          "& > tr > td": {
            textAlign: "center",
          },
          "& > tr > td:not(:first-of-type)": {
            borderLeft: `solid 1px ${palette.mystic}`,
            borderRight: `solid 1px ${palette.mystic}`,
            borderBottom: `solid 1px ${palette.mystic}`,
          },
          "& > tr:hover": {
            backgroundColor: "rgb(241, 244, 248, 0.5)",
          },
          "& > tr:last-of-type > td": {
            borderBottom: "none",
          },
        }}
      >
        {financials.map((financialStatementRow) => {
          return (
            <tr
              key={financialStatementRow.fiscalYear}
              css={{
                "& > td": {
                  padding: "8px 12px",
                },
              }}
            >
              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  backgroundColor: palette.blueBayoux,
                  fontWeight: 600,
                  color: palette.white,
                  lineHeight: "20px",
                  fontSize: 14,
                  borderTop: `solid 1px ${palette.lynch}`,
                  borderBottom: `solid 1px ${palette.lynch}`,
                  borderRight: `solid 1px ${palette.lynch}`,
                }}
              >
                {financialStatementRow.isTrailingTwelveMonths ? (
                  <TextTooltip
                    id={`tooltips/th/trailingTwelveMonths-${financialStatementRow.endOfPeriod}`}
                    placement="top"
                    content="Acumulado dos últimos 12 meses"
                    style={{
                      maxWidth: 260,
                      textAlign: "center",
                    }}
                  >
                    {({ accessibility, bind, ref }) => (
                      <span {...accessibility} {...bind} ref={ref}>
                        {format(parse(financialStatementRow.endOfPeriod, "yyyy-MM-dd", new Date()), "MM/yy 'TTM'")}
                      </span>
                    )}
                  </TextTooltip>
                ) : (
                  <span>{format(parse(financialStatementRow.endOfPeriod, "yyyy-MM-dd", new Date()), "MM/yyyy")}</span>
                )}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.netEquity < 0 ? palette.mojo : palette.punga,
                  lineHeight: "20px",
                  fontSize: 14,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.netEquity)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.fruitSalad,
                  lineHeight: "20px",
                  fontSize: 14,
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.financialIntermediationRevenue)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.netIncome >= 0 ? palette.oceanGreen : palette.mojo,
                  lineHeight: "20px",
                  fontSize: 14,
                  border: `solid 1px ${palette.fringyFlower}`,
                  borderRight: "none",
                }}
              >
                {formatFinancialValue(financialStatementRow.netIncome)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  color:
                    financialStatementRow.netMargin < 0 || financialStatementRow.netIncome < 0
                      ? palette.mojo
                      : palette.blueBayoux,
                  fontWeight: financialStatementRow.netMargin < 0 || financialStatementRow.netIncome < 0 ? 600 : 500,
                  lineHeight: "20px",
                  borderLeft: `solid 1px ${palette.mystic}`,
                  border: `solid 1px ${palette.mystic}`,
                  backgroundColor: "#fafafa",
                }}
              >
                {financialStatementRow.netMargin < 0 || financialStatementRow.netIncome < 0
                  ? "P"
                  : `${(financialStatementRow.netMargin || 0)
                      .toFixed((financialStatementRow.netMargin || 0) < 1 ? 1 : 0)
                      .replace(".", ",")}%`}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight:
                    (financialStatementRow.returnOnEquity || 0) < 0 ||
                    financialStatementRow.netIncome < 0 ||
                    financialStatementRow.netEquity < 0
                      ? 600
                      : 500,
                  color:
                    (financialStatementRow.returnOnEquity || 0) < 0 ||
                    financialStatementRow.netIncome < 0 ||
                    financialStatementRow.netEquity < 0
                      ? palette.mojo
                      : palette.blueBayoux,
                  lineHeight: "20px",
                  borderLeft: `solid 1px ${palette.mystic}`,
                  border: `solid 1px ${palette.mystic}`,
                  borderRight: "none",
                  backgroundColor: "#fafafa",
                }}
              >
                {(financialStatementRow.returnOnEquity || 0) < 0 ||
                financialStatementRow.netIncome < 0 ||
                financialStatementRow.netEquity < 0
                  ? "P"
                  : `${(financialStatementRow.returnOnEquity || 0)
                      .toFixed((financialStatementRow.returnOnEquity || 0) < 1 ? 1 : 0)
                      .replace(".", ",")}%`}
              </td>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.bisRatio}
                comparators={[
                  (value) => {
                    return value < 11;
                  },
                  (value) => {
                    return value >= 11;
                  },
                ]}
                styles={[
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                ]}
              >
                {(bisRatio, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 600,
                      color: palette.dixie,
                      lineHeight: "20px",
                      fontSize: 14,
                      border: `solid 1px #eec9c9`,
                      ...css,
                    }}
                  >
                    {bisRatio === null ? "-" : `${bisRatio}%`}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.mojo,
                  lineHeight: "20px",
                  fontSize: 14,
                  border: `solid 1px #eec9c9`,
                }}
              >
                {formatFinancialValue(financialStatementRow.provisionForCreditLosses)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.provisionForCreditLossesToNetIncomeRatio}
                comparators={[
                  (value) => {
                    return value > 0 && value < 75;
                  },
                  (value) => {
                    return value >= 75 && value < 100;
                  },
                  (value) => {
                    return value >= 100;
                  },
                ]}
                styles={[
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                ]}
              >
                {(provisionForCreditLossesToNetIncomeRatio, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 600,
                      color: palette.mojo,
                      lineHeight: "20px",
                      fontSize: 14,
                      border: `solid 1px #eec9c9`,
                      ...css,
                    }}
                  >
                    {(provisionForCreditLossesToNetIncomeRatio || 0) < 0
                      ? "P"
                      : `${provisionForCreditLossesToNetIncomeRatio}%`}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              <td
                css={{
                  color: palette.luxorGold,
                  fontWeight: 500,
                  fontSize: 14,
                  fontFamily: "novel-mono-pro-condensed",
                  lineHeight: "20px",
                  border: `solid 1px ${palette.chamois}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.cashFlowForDividends || financialStatementRow.payout)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={
                  financialStatementRow.cashFlowForDividendsToNetIncomeRatio ||
                  financialStatementRow.payoutToNetIncomeRatio
                }
                comparators={[
                  () => {
                    return financialStatementRow.netIncome < 0;
                  },
                  (value) => {
                    return value > 0 && value < 30;
                  },
                  (value) => {
                    return value >= 30 && value < 70;
                  },
                  (value) => {
                    return value >= 70;
                  },
                ]}
                styles={[
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                ]}
              >
                {(payoutOrCashFlowForDividendsToNetIncomeRatio, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 500,
                      color: palette.luxorGold,
                      lineHeight: "20px",
                      fontSize: 14,
                      border: `solid 1px ${palette.chamois}`,
                      borderRight: "none",
                      ...css,
                    }}
                  >
                    {financialStatementRow.netIncome < 0 ? "P" : `${payoutOrCashFlowForDividendsToNetIncomeRatio}%`}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
